import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dbad5vm9if4sq287d0t0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [chars, setChars] = useState(0);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (status === "submitting") return;

    const formData = new FormData(formEl);
    const honey = String(formData.get("contact_alt") || "").trim();
    if (honey) {
      setStatus("success");
      setMessage("反馈已提交，感谢你的支持！");
      formEl.reset();
      setChars(0);
      return;
    }

    formData.delete("contact_alt");
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const failed =
        !response.ok ||
        parsed?.code !== "OK" ||
        serverMsg.toLowerCase().includes("spam");

      if (failed) {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试。");
        return;
      }

      setStatus("success");
      setMessage("反馈已提交，感谢你的支持！我们会尽快查看。");
      formEl.reset();
      setChars(0);
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <form
      id="contact-feedback-form"
      data-readdy-form
      onSubmit={onSubmit}
      className="rounded-lg bg-background-50 border border-background-200/80 p-5 md:p-6"
    >
      <h3 className="font-heading text-base font-bold text-foreground-950">留言反馈</h3>
      <p className="mt-1.5 text-xs text-foreground-500">发现问题或有想看的内容？给我们留言，我们会定期查看。</p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="contact-name" className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="例如：小林"
            className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
          />
        </div>
      </div>

      <div className="mt-3">
        <label htmlFor="contact-topic" className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">
          反馈类型
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue="内容建议"
          className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors cursor-pointer"
        >
          <option value="内容建议">内容建议</option>
          <option value="信息纠错">信息纠错</option>
          <option value="功能反馈">功能反馈</option>
          <option value="合作咨询">合作咨询</option>
        </select>
      </div>

      <div className="mt-3">
        <label htmlFor="contact-message" className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">
          留言内容 <span className="text-foreground-400 font-normal">（最多 500 字）</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          onChange={(e) => setChars(e.target.value.length)}
          placeholder="请描述你遇到的问题或想看的内容…"
          className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors resize-none leading-relaxed"
        />
        <div className="mt-1 text-right text-[11px] font-label text-foreground-400">{chars} / 500</div>
      </div>

      <input
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="contact-alt-field"
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-md bg-primary-600 text-background-50 text-sm font-label font-semibold whitespace-nowrap hover:bg-primary-700 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center" />
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center" />
            提交反馈
          </>
        )}
      </button>

      {message && (
        <p
          className={`mt-3 text-xs leading-relaxed ${
            status === "success" ? "text-primary-700" : "text-accent-800"
          }`}
          role="status"
        >
          {message}
        </p>
      )}
    </form>
  );
}